"use strict";

const JSZip = require("jszip");

// Converts data from a ReadableStream object
// to a Buffer
const streamToBuffer = async (stream) => {
	let buffers = [];
	let reader = stream.getReader();
	while (true)
	{
		const { done, value } = await reader.read();
		if (done) {
			break;
		}
		buffers.push(Buffer.from(value));
	}
	let buffer = Buffer.concat(buffers);
	return buffer;
};

const readFileToBuffer = async function(file)
{
	return new Promise((resolve, reject) => {
		var reader = new FileReader();
		reader.onerror = function() {
			reject(reader.error);
		};
		reader.onload = function(e) {
			resolve(reader.result);
		}
		reader.readAsArrayBuffer(file);
	});
};

// Loads a zip archive from File submitted by user.
// Returns an object representing the zip archive.
const loadZipArchive = async function(file)
{
	let zip = new JSZip();
    let zipData = await zip.loadAsync(file);
	return zipData;
};

// Adds a file inside a zip file
// zipArchive: jszip object representing the zip archive
// fileName: Name of the file to be added inside the zip file
// fileContent: The content of the file to be added
// Returns the content of the zip file as an arraybuffer
const addFileToZip = async function(zipArchive, filename, fileContent)
{
    // Add the file inside the zip file
    zipArchive.file(filename, fileContent);

    // Recreate the zip file
    const modifiedZipData = await zipArchive.generateAsync({
		type: 'arraybuffer',
		compression: "DEFLATE"
	});

	return modifiedZipData;
};

// Zip files into a single archive while preserving (optional) folder structure.
// Inputs:
// - files: Either
//     a) an array-like of File objects (legacy path), or
//     b) an array of items shaped like { file: File, path: string } where
//        `path` is a relative path using forward slashes.
// - extraFileName: Optional name of an additional file to add at the zip root
// - extraFileContent: Content of that additional file
//
// Notes:
// - We accept both input shapes to remain backward compatible with existing
//   callers while enabling the new folder upload pipeline.
// - For legacy File[], we derive each path from file.webkitRelativePath (when
//   provided by <input webkitdirectory>) or fall back to file.name.
// - Paths are normalized to keep the archive safe and consistent:
//   forward slashes, no leading slash, collapse repeated slashes, and no '..'.
// - We add files sequentially to limit peak memory usage.
const zipFiles = async function (files, extraFileName, extraFileContent)
{
    const zip = new JSZip();

    // Helper: normalize a prospective zip path
    const normalizeZipPath = (p) => Util.normalizeRelativePath(p);

    // Iterate and add file contents to the archive
    const n = files ? files.length : 0;
    for (let i = 0; i < n; i++) {
        const item = files[i];
        // Determine input shape
        const isStructured = item && typeof item === 'object' && 'file' in item && 'path' in item;
        const fileObj = isStructured ? item.file : item;
        if (!fileObj) continue;
        // Derive the intended path inside the archive
        let path = isStructured ? item.path : (fileObj.webkitRelativePath || fileObj.name);
        path = normalizeZipPath(path);
        if (!path || path.indexOf('..') !== -1) {
            console.warn('Skipping unsafe or empty path inside zip:', path);
            continue;
        }
        // Read content as ArrayBuffer to ensure compatibility with FileSystemEntry-based Files
        // Some environments can throw NotFoundError when JSZip reads Blob lazily from
        // FileSystem entries during generateAsync. Reading eagerly avoids that.
        const buffer = await readFileToBuffer(fileObj);
        zip.file(path, buffer);
    }

    if (extraFileName != null)
    {
        // Add the extra metadata file at the zip root
        zip.file(extraFileName, extraFileContent);
    }

    /*
    Output type options for generateAsync:
    base64 | binarystring | array | uint8array | arraybuffer | blob | nodebuffer
    */
    const content = await zip.generateAsync({
        type: 'uint8array',
        compression: "DEFLATE"
    });

    // Example: to allow users to download the zip directly, one could use FileSaver
    // FileSaver.saveAs(content, 'download.zip');
    // (FileSaver must be imported at top if used.)

    return content;
};

// Gets filenames inside zip file
// zipFileContent: Then zip file bytes
const getFilenamesInZip = async function(zipFileContent)
{
	return new Promise((resolve, reject) => {
		let filenames = [];
		try
		{
			JSZip.loadAsync(zipFileContent).then(function(zip) {
				for (let [filename, file] of Object.entries(zip.files)) {
					filenames.push(filename);
				}
				resolve(filenames);
			}).catch(function(err) {
				reject(err);
			});
		}
		catch(err)
		{
			reject(err);
		}
	});
};

// Get file in zip given the ending of the filename. E.g. the extension of the file
// or just some string that the filename ends with.
// zipFileContent: bytes of the zip file
// fileEnding: String that the filename ends with
// Returns the first file whose name ends with fileEnding
// If no file is found then null is returned
const getFileInZipByEnding = async function(zipFileContent, fileEnding)
{
	return new Promise((resolve, reject) => {
		let fileFound = null;
		try
		{
			JSZip.loadAsync(zipFileContent).then(function(zip) {
				for (let [filename, file] of Object.entries(zip.files)) {
					if (filename.endsWith(fileEnding))
					{
						fileFound = file;
						break;
					}
				}
				resolve(fileFound);
			}).catch(function(err) {
				reject(err);
			});
		}
		catch(err)
		{
			reject(err);
		}
	});
};

// Infer the icon string to use for cloud entry
// given the contents of a .arkioimport file
// as a string
const inferIconFromArkioImport = function(arkioImportStr)
{
	let icon = null;
	let arkioImport = null;
	try {
		arkioImport = JSON.parse(arkioImportStr);
	} catch (e) {
		return null;
	}
	if (arkioImport.Product != null)
	{
		switch (arkioImport.Product)
		{
			case "Revit":
				icon = Constants.Icon.Revit;
				break;
			case "Rhino":
				icon = Constants.Icon.Rhino;
				break;
			case "SketchUp":
				icon = Constants.Icon.SketchUp;
				break;
			case "Archicad":
				icon = Constants.Icon.Archicad;
				break;
			case "Navisworks":
				icon = Constants.Icon.Navisworks;
				break;
			default:
				break;
		}
	}
	return icon;
};

// Check zip file content
// and return icon string based on contents of .arkioimport
// file if it is found inside the zip
// zipFileContent: bytes of a zip file
const inferIconFromZip = async function(zipFileContent)
{
	// try finding a file in the zip that ends with ".arkioimport"
	let file = await getFileInZipByEnding(zipFileContent, ".arkioimport");
	if (file != null)
	{
		let text = await file.async('string');
		let icon = inferIconFromArkioImport(text);
		return icon;
	}
	return null;
};

const inferIconFromFiles = async function(files)
{
	let n = files.length;
	for(let i = 0; i < n; i++)
	{
		if(files[i].name.endsWith(".arkioimport"))
		{
			let buffer = await readFileToBuffer(files[i]);
			let uint8arr = new Uint8Array(buffer);
			let str = new TextDecoder().decode(uint8arr);
			let icon = inferIconFromArkioImport(str);
			return icon;
		}
	}
	return null;
};

const processIssuesZip = async (zipFileContent) => {
    const zip = await JSZip.loadAsync(zipFileContent);
    
    let jsonFile = null;
    for (const filename in zip.files) {
        if (filename.endsWith(".json")) {
            jsonFile = zip.files[filename];
            break;
        }
    }

    if (!jsonFile) {
        throw new Error("No .json file found in the archive.");
    }

    const jsonString = await jsonFile.async('string');
    const issueData = JSON.parse(jsonString);

    if (issueData.Issues) {
        for (const issueId in issueData.Issues) {
            const issue = issueData.Issues[issueId];
            if (issue.ViewPointImageFilename) {
                const imageFile = zip.file(issue.ViewPointImageFilename);
                if (imageFile) {
                    const base64 = await imageFile.async('base64');
                    issue.ViewPointImageDataBase64 = `data:image/jpeg;base64,${base64}`;
                }
            }
        }
    }

    return issueData;
};

// Returns a fresh JSZip instance. This exists so plain (non-CommonJS)
// scripts like ExportUtil.js can build zip archives without needing
// access to the bundled JSZip module - they call DataUtil.createZip()
// since DataUtil is exposed on window by main.js.
const createZip = () => new JSZip();

module.exports = {
	streamToBuffer : streamToBuffer,
	readFileToBuffer : readFileToBuffer,
	zipFiles : zipFiles,
	getFilenamesInZip : getFilenamesInZip,
	getFileInZipByEnding : getFileInZipByEnding,
	inferIconFromZip : inferIconFromZip,
	inferIconFromFiles : inferIconFromFiles,
	addFileToZip : addFileToZip,
	loadZipArchive : loadZipArchive,
	processIssuesZip: processIssuesZip,
	createZip : createZip
};
